<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import AddLabel from './AddLabel.vue';
import EditLabel from './EditLabel.vue';
import PageLayout from 'dashboard/components-next/layout/PageLayout.vue';
import PageHeader from 'dashboard/components-next/layout/PageHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedLabel = ref({});

const records = computed(() => getters['labels/getLabels'].value);
const uiFlags = computed(() => getters['labels/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedLabel.value.title}?`);

const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedLabel.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedLabel.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteLabel = async id => {
  try {
    await store.dispatch('labels/delete', id);
    useAlert(t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedLabel.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedLabel.value.id] = true;
  closeDeletePopup();
  deleteLabel(selectedLabel.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('LABEL_MGMT.LIST.TABLE_HEADER.NAME'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.COLOR'),
  ];
});

const route = useRoute();
const router = useRouter();

// Abre o modal de edit automaticamente quando a rota chega com `?edit=<id>`.
// Usado pela sidebar (action "Editar etiqueta" em cada label child) pra
// pular o passo de localizar a label na tabela admin.
const maybeOpenEditFromQuery = async () => {
  const editId = Number(route.query.edit);
  if (!editId) return;
  // Garante que records ja carregou antes de procurar.
  if (records.value.length === 0) await store.dispatch('labels/get');
  const label = records.value.find(l => l.id === editId);
  if (label) openEditPopup(label);
  // Limpa o query param pra evitar re-abrir o modal em navegacoes futuras
  // dentro da mesma rota.
  router.replace({ query: { ...route.query, edit: undefined } });
};

// Sidebar dispara este evento via createSidebarActionHandler.new-label
// (item "Etiquetas" no menu Settings). Permissao ja foi checada na sidebar
// (requiredPermissions = SETTINGS_ACCESS_PERMISSIONS).
const handleSidebarLabelNew = () => {
  showAddPopup.value = true;
};

onBeforeMount(async () => {
  emitter.on(BUS_EVENTS.SIDEBAR_OPEN_LABEL_NEW, handleSidebarLabelNew);
  await store.dispatch('labels/get');
  maybeOpenEditFromQuery();
});

onBeforeUnmount(() => {
  emitter.off(BUS_EVENTS.SIDEBAR_OPEN_LABEL_NEW, handleSidebarLabelNew);
});

// Caso a sidebar dispare nova navegacao pra mesma rota com outro `edit`,
// o componente nao remontou — reagir via watch.
watch(
  () => route.query.edit,
  () => maybeOpenEditFromQuery()
);
</script>

<template>
  <PageLayout>
    <template #header>
      <PageHeader
        icon="i-lucide-tag"
        accent="amber"
        :title="$t('LABEL_MGMT.HEADER')"
        :subtitle="$t('LABEL_MGMT.DESCRIPTION')"
      />
    </template>
    <woot-loading-state
      v-if="uiFlags.isFetching"
      :message="$t('LABEL_MGMT.LOADING')"
    />
    <p
      v-else-if="!records.length"
      class="flex flex-col items-center justify-center h-full text-base text-n-slate-12 p-8"
    >
      {{ $t('LABEL_MGMT.LIST.404') }}
    </p>
    <table v-else class="min-w-full overflow-x-auto divide-y divide-n-weak">
      <thead>
        <th
          v-for="thHeader in tableHeaders"
          :key="thHeader"
          class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
        >
          {{ thHeader }}
        </th>
      </thead>
      <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
        <tr v-for="(label, index) in records" :key="label.title">
          <td class="py-4 ltr:pr-4 rtl:pl-4">
            <span class="mb-1 font-medium break-words text-n-slate-12">
              {{ label.title }}
            </span>
          </td>
          <td class="py-4 ltr:pr-4 rtl:pl-4">{{ label.description }}</td>
          <td class="py-4 leading-6 ltr:pr-4 rtl:pl-4">
            <div class="flex items-center">
              <span
                class="w-4 h-4 mr-1 border border-solid rounded rtl:mr-0 rtl:ml-1 border-n-weak"
                :style="{ backgroundColor: label.color }"
              />
              {{ label.color }}
            </div>
          </td>
          <td class="py-4 min-w-xs">
            <div class="flex gap-1 justify-end">
              <Button
                v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
                icon="i-lucide-pen"
                slate
                xs
                faded
                :is-loading="loading[label.id]"
                @click="openEditPopup(label)"
              />
              <Button
                v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                :is-loading="loading[label.id]"
                @click="openDeletePopup(label, index)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddLabel @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditLabel :selected-response="selectedLabel" @close="hideEditPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('LABEL_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('LABEL_MGMT.DELETE.CONFIRM.NO')"
    />
  </PageLayout>
</template>
